import { Controller } from "@hotwired/stimulus"

// Plays the flask's own 24 second loop in the browser tab: bubbles, a sway, a
// near explosion that fizzles out and refills, two hops. A loop for the tab
// alone, `brand/flask-loop` in the pixel icon library, whose strip and frame
// length the layout passes in; the header keeps its short bubbling loop and
// the brand kit its still flask.
//
// **The static icon is the one every crawler sees, and it stays in the HTML.**
// The <link rel="icon"> tags in the layouts name the still flask, and this
// controller only swaps the SVG link's address at runtime, in a browser that is
// actually showing the page. A search engine reads the HTML and never runs
// this, so what it indexes is the still drawing. The red circle Google once
// showed for this site was not an animation going wrong: it was Rails' default
// icon.png, which the HTML named as the raster icon until 2026-10-07.
//
// With no JavaScript, under reduced motion, or if the strip fails to load,
// nothing is swapped and the still flask stays in the tab.
//
// **The link is looked up on every frame, never held.** Turbo merges the head
// on each visit by comparing elements, and ours no longer matches the next
// page's once its address is a frame, so Turbo removes it and inserts that
// page's still link in its place. A controller holding the first link went on
// painting a detached element after the first click while the worker kept
// ticking, and the tab sat on the still flask. Finding the current link each
// time animates whichever one the head holds now. The still address is
// remembered from the server's markup and never from a link that arrives
// carrying a frame, which is what a restored snapshot's head holds.
//
// Canvas plus an href swap is the only way Chrome animates a favicon at all:
// it shows one frame of a GIF and rasterises an SVG once.
//
// **The tick comes from a worker, and the frame from the clock.** A page's own
// timers are throttled in a background tab, once a second and after five
// minutes once a minute, which is exactly where a favicon is all there is of
// the site: driven from the page, the bubbles slowed down when the tab was left,
// raced to catch up on return, and after a while looked stopped. A worker's
// timer is not throttled that way, so `favicon_clock.js` ticks and this draws.
// The frame is the time divided by the frame length rather than a counter, so
// a late or doubled tick can never speed the loop up or slow it down. Where a
// worker cannot start, the page's own timer stands in.
const GRID = 16
const SIZE = 32

export default class extends Controller {
  static values = { strip: String, clock: String, frameMs: { type: Number, default: 100 } }

  connect() {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return

    const link = this.#currentLink()
    if (!link || !this.stripValue) return

    this.staticHref = link.getAttribute("href")
    this.canvas = document.createElement("canvas")
    this.canvas.width = SIZE
    this.canvas.height = SIZE
    this.paint = this.canvas.getContext("2d")
    if (!this.paint) return
    this.paint.imageSmoothingEnabled = false

    this.strip = new Image()
    // The strip comes from the art CDN, another host, and a canvas that has
    // drawn another host's picture refuses toDataURL unless the picture was
    // asked for with CORS. The CDN answers `Access-Control-Allow-Origin: *`.
    // Without this the first frame threw and the tab kept the still flask.
    this.strip.crossOrigin = "anonymous"
    this.strip.onload = () => this.#start()
    this.strip.src = this.stripValue
  }

  disconnect() {
    clearInterval(this.timer)
    this.timer = null
    this.clock?.terminate()
    this.clock = null
    if (this.strip) this.strip.onload = null
    const link = this.#currentLink()
    if (link && this.staticHref) link.href = this.staticHref
  }

  #currentLink() {
    return document.querySelector('link[rel="icon"][type="image/svg+xml"]')
  }

  #start() {
    // The frame count is the strip's own width, so a regenerated loop of
    // another length plays whole without an edit here.
    this.frames = Math.floor(this.strip.naturalWidth / GRID)
    if (this.frames < 2) return

    this.#draw()
    try {
      this.clock = new Worker(this.clockValue)
      this.clock.onmessage = () => this.#draw()
      this.clock.onerror = () => this.#fallBack()
    } catch {
      this.#fallBack()
    }
  }

  #fallBack() {
    this.clock?.terminate()
    this.clock = null
    if (!this.timer) this.timer = setInterval(() => this.#draw(), this.frameMsValue)
  }

  #draw() {
    const link = this.#currentLink()
    if (!link) return

    // A link Turbo has just inserted shows the still flask until it is painted,
    // so it is painted at once rather than at the next change of frame.
    const fresh = link !== this.link
    if (fresh) {
      const href = link.getAttribute("href")
      if (href && !href.startsWith("data:")) this.staticHref = href
      this.link = link
    }

    const frame = Math.floor(Date.now() / this.frameMsValue) % this.frames
    if (frame === this.frame && !fresh) return

    this.frame = frame
    this.paint.clearRect(0, 0, SIZE, SIZE)
    this.paint.drawImage(this.strip, frame * GRID, 0, GRID, GRID, 0, 0, SIZE, SIZE)
    link.href = this.canvas.toDataURL("image/png")
  }
}
